/**
 * One task, everywhere the studio shows one — `.docs/20` §Before fan-out 3's
 * "`TaskCardFull` (board card + the detail drawer)".
 *
 * The card itself is session 09's `TaskCard` primitive and is not re-implemented
 * here. What this file owns is the four **readings** of a `TaskView` that the board,
 * the list table and the detail page each need, and which were written out three
 * times before they were one:
 *
 *   - **`taskState`** — which rail. `mockups/studio/tasks.html`'s own fold is the
 *     specification: "A red rail means genuinely broken: a task past its date, or a
 *     payment that failed. An ochre rail means the next move is the client's."
 *   - **`taskUnder`** — the `.tcard__client` line: what the task is work on.
 *   - **`TaskWhen`** — "Due 21 Aug · 3 d late", "Waiting on them", "Due 4 Sep".
 *   - **`TaskWho`** — the `.avatar` for a person, and words for everybody else.
 *
 * **Nothing here computes a figure.** `daysLate` is date arithmetic through
 * `packages/shared/src/dates.ts` and nowhere else — `Math.floor` is lint-banned in
 * this repository, including in the tests, which is why `daysBetween` exists.
 *
 * **The gap this file used to describe is closed.** `TaskSchema` now carries
 * `assignee_name` beside `assignee_id`, so every card can say whose it is rather
 * than only the reader's own. Three rules came out of putting it there:
 *
 *   1. **An agent is not a person.** `Avatar` draws initials in a circle and the
 *      circle is a face; "delivery-bot" has no initials and is not somebody you can
 *      ask. An agent is a *name in words*, qualified — "delivery-bot — an agent" —
 *      so that a coordinator scanning six columns can see at a glance which cards
 *      are being worked by software.
 *   2. **A name is not a claim.** `tasks_assign` names somebody and leaves the task
 *      `open` with no lease; `tasks_claim` is what takes it. `holdWords` is the
 *      difference, said only when there is something to say (P3 — show the
 *      exception, fold the routine).
 *   3. **An absence is muted; a holder is not.** `TaskWhen` already writes "No date"
 *      in grey and a real date in body text, and the same register separates
 *      "Unassigned" — nobody has this — from "A studio member", which is a holder
 *      whose `user` row has gone. Two different facts, two different weights.
 *
 * How much of a name each surface prints is the surface's own decision (DESIGN.md
 * P5): the board is six columns wide and gets `initialsOf`, the list table and the
 * detail page have room and get the whole name. P6 keeps it out of mono — a name is
 * not a figure, a date or a reference.
 */

import { Link } from '@tanstack/react-router'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import {
  dateOnlyOf,
  daysBetween,
  isRef,
  startOfDayIn,
  type AssigneeKind,
  type TaskView,
} from '@fareast/shared'

import { usePrincipal, type Principal } from '../../auth/principal.js'
import { useFormat } from '../../format/useFormat.js'
import { now } from '../../integrations/clock.js'
import { Avatar, TaskCard, notePreview, type TaskCardState } from '../portal/Board.js'
import { DateText, Linkish, Muted } from '../portal/Text.js'
import { ActionDialog } from './ActionDialog.js'
import { AssignDialog } from './AssignDialog.js'
import { CellDue } from './CellEdit.js'
import { MoneyTag } from './MoneyTag.js'
import { AGENT_NOUN, QUEUE_WORD } from './taskWords.js'

export { AGENT_NOUN, PRIORITY_WORD, QUEUE_WORD, STATUS_WORD, priorityWord } from './taskWords.js'

/* --------------------------------------------------------------- the words */

/** Whose move it is, said as a sentence rather than as a field name. */
export const WAITING_WORD: Readonly<Record<'client' | 'staff' | 'external', string>> = {
  client: 'Waiting on them',
  staff: 'Waiting on us',
  external: 'Waiting on a third party',
}

/** The two states a task is finished in. Cancelled never appears; done only under Completed. */
export function isLive(task: TaskView): boolean {
  return task.status !== 'done' && task.status !== 'cancelled'
}

/* -------------------------------------------------------------- the readings */

/**
 * Past its date and not finished.
 *
 * `ARCHITECTURE.md` §The task queue: overdue "is derived from `due_at`, never
 * stored" — so it is derived here too, from the same two things the API compares.
 */
export function isOverdue(task: TaskView, at: Date): boolean {
  return isLive(task) && task.due_at !== null && new Date(task.due_at).getTime() < at.getTime()
}

/**
 * How urgent the due date is for triage colour (#21).
 *
 * `overdue` — past `at` (same as `isOverdue`). `today` — due on the reader's calendar
 * day. `soon` — due tomorrow or the day after. Null when undated, finished, or further
 * out: those stay plain body text.
 */
export type DueUrgency = 'overdue' | 'today' | 'soon'

/** Calendar days ahead that still count as approaching (today excluded). */
export const DUE_SOON_DAYS = 2

export function dueUrgency(task: TaskView, at: Date, zone: string): DueUrgency | null {
  if (!isLive(task) || task.due_at === null) return null
  if (isOverdue(task, at)) return 'overdue'
  const due = startOfDayIn(dateOnlyOf(new Date(task.due_at), zone), zone)
  const today = startOfDayIn(dateOnlyOf(at, zone), zone)
  const ahead = daysBetween(today, due)
  if (ahead === 0) return 'today'
  if (ahead > 0 && ahead <= DUE_SOON_DAYS) return 'soon'
  return null
}

/**
 * A payment that failed: a finance task whose money is an invoice.
 *
 * The mockup's red rail is "a task past its date, **or a payment that failed**", and
 * this is the second half. There is no `payment_failed` flag on a task — what there
 * is, is the fact that `payment.charge_failed` raises a `finance` task carrying the
 * invoice as its `money_ref` (session 15), and that is what this reads.
 */
export function isPaymentFailure(task: TaskView): boolean {
  return (
    isLive(task) &&
    task.queue === 'finance' &&
    task.money_ref !== null &&
    isRef('invoice', task.money_ref)
  )
}

/**
 * Which rail. `bad` wins over `need`: a late approval is late first.
 *
 * `undefined` is the third state and the mockup is explicit about it — "Everything
 * else is routine, and the card says so in words." P4: good is the absence of ochre.
 */
export function taskState(task: TaskView, at: Date): TaskCardState | undefined {
  if (isOverdue(task, at) || isPaymentFailure(task)) return 'bad'
  if (task.waiting_on === 'client') return 'need'
  return undefined
}

/**
 * What a task is work on, or **null when the only honest answer is its queue**.
 *
 * `taskUnder` below is this with the queue as its fallback, which is right for a card
 * that has no other line to say it on. It is wrong for the detail page, which prints
 * a *Queue* row of its own: session 24 (R2-21) found a task with no service reading
 * "Under: Delivery" directly above "Queue: Delivery" — one value under two labels,
 * which is DESIGN.md P2 broken (a fact has one owner). A surface with a Queue row
 * reads this one and renders nothing when it answers null.
 *
 * A `WO-` reference **is** a project by construction (`work_order_ref_matches_kind`),
 * which is why it is worded as one rather than by its service: on the mockup that
 * card reads "Project · WO-2026-011" and the distinction it is drawing is that this
 * one bills on its own invoice.
 *
 * `serviceName` comes from `catalog_list_services` and is the card's own name for the
 * `service_code` column. The page passes it in rather than this looking it up, so the
 * list table and the board share one read.
 */
export function taskUnderOrNull(task: TaskView, serviceName: string | undefined): string | null {
  const ref = task.money_ref
  if (ref !== null && isRef('work_order', ref)) return `Project · ${ref}`
  if (ref !== null && isRef('request', ref)) {
    return `${serviceName ?? QUEUE_WORD[task.queue]} · ${ref}`
  }
  return serviceName ?? null
}

/** The `.tcard__client` line. `taskUnderOrNull`, and the queue when it has nothing. */
export function taskUnder(task: TaskView, serviceName: string | undefined): string {
  return taskUnderOrNull(task, serviceName) ?? QUEUE_WORD[task.queue]
}

/**
 * How many days late, counted in **calendar days in the reader's own zone**.
 *
 * `daysBetween` on the two instants would answer 2 for a task due at 16:00 on the
 * 21st read at noon on the 24th, and the mockup says "3 d late" — because a person
 * counting lateness counts days on a calendar, not elapsed 24-hour blocks. Both ends
 * are pushed to the start of their own day first, which is the same trick
 * `dates.ts` `isDayBefore` documents for a `DateOnly` comparison.
 */
export function daysLate(task: TaskView, at: Date, zone: string): number {
  if (task.due_at === null) return 0
  const due = startOfDayIn(dateOnlyOf(new Date(task.due_at), zone), zone)
  return daysBetween(due, startOfDayIn(dateOnlyOf(at, zone), zone))
}

/* ------------------------------------------------------------- the fragments */

export type TaskWhenProps = {
  readonly task: TaskView
  /** The instant "late" is measured against. The page reads the clock once. */
  readonly at: Date
  /**
   * Drop the word "Due" (session 38).
   *
   * A card has no column head, so it has to say what the date is; the list has one
   * above every row, and repeating "Due" thirty times is the header said again in
   * each cell. The reading is otherwise identical, which is why this is a prop rather
   * than a second component.
   */
  readonly bare?: boolean | undefined
}

/**
 * The date half of a card's meta row.
 *
 * Four sentences, in the order the mockup ranks them: how late it is, whose move it
 * is, when it is due, and — for a task with neither — that it has no date, which is
 * a fact rather than a blank.
 *
 * Urgency (#21): overdue uses `--bad-ink` plus a mark; due today / within two days
 * uses `--need-ink` plus a mark — colour and shape on the words, not a second rail.
 * The sentence still carries the meaning for anyone who cannot see colour.
 *
 * **The hour is part of the date** (the owner, 17 Sep: "mostra nuovamente l'orario
 * nella colonna Due"). A task is due at a time, the editor in the cell has always
 * held one, and the reading beside it printed the day alone — so a task due at 09:00
 * and one due at 18:00 read as the same task. `stamp` is the style `useFormat` keeps
 * for this: `18 Aug 2026, 18:00 CEST`, the zone included, because session 24's rule
 * is that no time of day is rendered in this portal without the clock it is on.
 *
 * **The clause sits under the date, not beside it** (the owner, 17 Sep). The hour
 * made the one-line reading long enough to set the width of the whole Due column,
 * and the column was paying for the shortest phrase in it — "· 6 d late".
 * `task-when__note` is a block, so the stamp keeps one line and the clause takes the
 * next; the column is then as wide as the stamp and no wider.
 */
export function TaskWhen({ task, at, bare }: TaskWhenProps) {
  const lead = bare === true ? null : 'Due '
  const format = useFormat()
  const urgency = dueUrgency(task, at, format.zone)
  const tone =
    urgency === 'overdue'
      ? 'task-when task-when--late'
      : urgency === 'today' || urgency === 'soon'
        ? 'task-when task-when--soon'
        : 'task-when'
  const mark = urgency === 'overdue' ? '●' : urgency === 'today' || urgency === 'soon' ? '▲' : null

  if (isOverdue(task, at)) {
    const late = daysLate(task, at, format.zone)
    return (
      <span className={tone}>
        {mark === null ? null : (
          <span className="task-when__mark" aria-hidden="true">
            {mark}{' '}
          </span>
        )}
        {lead}
        <DateText value={task.due_at ?? ''} style="stamp" nowrap />
        <span className="task-when__note">
          · {late === 0 ? 'past its time today' : `${format.number(late)} d late`}
        </span>
      </span>
    )
  }

  if (task.waiting_on !== null) return <span>{WAITING_WORD[task.waiting_on]}</span>

  if (task.due_at !== null) {
    const ahead =
      urgency === 'today' || urgency === 'soon'
        ? daysBetween(
            startOfDayIn(dateOnlyOf(at, format.zone), format.zone),
            startOfDayIn(dateOnlyOf(new Date(task.due_at), format.zone), format.zone),
          )
        : null
    return (
      <span className={tone}>
        {mark === null ? null : (
          <span className="task-when__mark" aria-hidden="true">
            {mark}{' '}
          </span>
        )}
        {lead}
        <DateText value={task.due_at} style="stamp" nowrap />
        {ahead === null ? null : (
          <span className="task-when__note">
            {ahead === 0 ? '· today' : ahead === 1 ? '· in 1 d' : `· in ${format.number(ahead)} d`}
          </span>
        )}
      </span>
    )
  }

  return (
    <span>
      <Muted>No date</Muted>
    </span>
  )
}

/** An actor, as any of the three surfaces needs to read one. */
export type Actor = {
  readonly kind: AssigneeKind
  readonly id: string | null
  readonly name: string | null
}

/**
 * What one `Actor` amounts to on the page.
 *
 * `person` and `agent` are kept apart rather than folded into one `name`, because
 * that separation *is* rule 1 in the header: only a person gets an `Avatar`, and a
 * caller that had one field would have to re-derive which kind it was holding.
 */
export type ActorReading = {
  /** The person whose initials an `Avatar` draws, or null. An agent is never one. */
  readonly person: string | null
  /** True when that person is whoever is reading the page. */
  readonly mine: boolean
  /** An agent we can name, or null. */
  readonly agent: string | null
  /** What to say when neither is a name — "Unassigned", "A studio member". */
  readonly words: string
}

/**
 * Who somebody is, given their kind, their id and the name the API sent.
 *
 * The name is the wire's, and the reader's own is the **fallback**: it is the one
 * studio name this process knows without being told, so a recording made before
 * `assignee_name` existed still draws the signed-in person's avatar rather than
 * losing them. Shared by the board card, the list table, the thread and the time log.
 */
export function describeActor(
  actor: Actor,
  principal: Principal,
  words: { readonly none: string; readonly user: string; readonly agent: string },
): ActorReading {
  if (actor.kind === 'agent') {
    return { person: null, mine: false, agent: actor.name, words: words.agent }
  }
  if (actor.kind === 'user') {
    const mine =
      actor.id !== null && principal.kind !== 'anonymous' && actor.id === principal.userId
    return {
      person: actor.name ?? (mine ? principal.name : null),
      mine,
      agent: null,
      words: words.user,
    }
  }
  return { person: null, mine: false, agent: null, words: words.none }
}

/** The noun that keeps an agent from reading as a colleague. */
/**
 * The actor as one line: the name, then whatever qualifies it.
 *
 * One builder for the avatar's accessible label, the list table's cell and the
 * thread's title, so that three surfaces cannot word the same three facts three
 * ways. `head — note, note` — and no dash at all when there is nothing to add,
 * which is the routine case and the quietest one.
 */
export function actorLine(who: ActorReading, hold: string | null = null): string {
  const head = who.person ?? who.agent ?? who.words
  const notes = [who.mine ? 'you' : null, who.agent === null ? null : AGENT_NOUN, hold].filter(
    (one): one is string => one !== null,
  )
  return notes.length === 0 ? head : `${head} — ${notes.join(', ')}`
}

/**
 * How firm the hold is, when there is anything to say — otherwise null.
 *
 * "Who is this assigned to" and "who is on it right now" are two questions, and
 * `TaskSchema` answers the second with `status` and `lease_expires_at` rather than
 * with a second person: an `open` task with a name on it is somebody's to pick up,
 * a `claimed` one has been picked up, and a lease in the past means it is about to
 * go back to the queue whatever the status says. Only an agent's lease: the sweep
 * leaves a person's task alone, so a lapsed date on it says nothing.
 *
 * **Null for the routine.** In session 18's world twenty-two of the twenty-seven
 * live tasks are assigned and unclaimed, so saying "assigned, not started" on
 * twenty-two cards would be P3's "if everything is flagged, nothing is flagged".
 * Null for `blocked`, too: `TaskWhen` owns whose move it is, and P2 gives a fact one
 * owner.
 */
export function holdWords(task: TaskView, at: Date): string | null {
  if (task.assignee_kind === 'none') return null
  if (
    task.assignee_kind === 'agent' &&
    task.lease_expires_at !== null &&
    new Date(task.lease_expires_at).getTime() <= at.getTime() &&
    isLive(task)
  ) {
    return 'nobody is working on it now'
  }
  if (task.status === 'claimed') return 'has it, not started'
  if (task.status === 'in_progress') return 'working on it'
  return null
}

/**
 * The list's one line under a name, or nothing (#28, correction round 1).
 *
 * Ferit asks that ownership-sensitive actions be hidden "with a concise explanation".
 * Roberto's reading, and the one the report records: the **name in the Who column is
 * the explanation**, so a task merely assigned to somebody says only their name. A
 * line is added just when a colleague is actually on it — claimed or in progress —
 * because that is the case where a reader might otherwise take it themselves.
 *
 * Nothing when the reader is the holder: the row already offers them their own next
 * move. A lapsed claim is the exception and speaks whoever holds it, because it is
 * the one state where a name on a task means nobody is doing it.
 */
export function holdLine(task: TaskView, at: Date, viewerId: string | null): string | null {
  if (holdWords(task, at) === null) return null
  /*
   * A lapsed claim speaks whoever holds it: it is the one state where a name on a
   * task means nobody is doing it. An agent's claim only, as in `holdWords`.
   */
  if (
    task.assignee_kind === 'agent' &&
    task.lease_expires_at !== null &&
    new Date(task.lease_expires_at).getTime() <= at.getTime() &&
    isLive(task)
  ) {
    return 'Nobody is working on it now'
  }
  const mine = task.assignee_kind === 'user' && task.assignee_id === viewerId
  if (mine) return null
  const who = task.assignee_name ?? ASSIGNEE_WORDS[task.assignee_kind]
  // Built from the status, not from `holdWords`' phrase: a card composes that phrase
  // after a dash ("Derya T. — working on it") and a line needs a verb of its own.
  if (task.status === 'claimed') return `${who} has it, not started`
  if (task.status === 'in_progress') return `${who} is working on it`
  return null
}

const ASSIGNEE_WORDS = {
  none: 'Unassigned',
  user: 'A studio member',
  agent: 'An agent',
} as const

/**
 * Who holds a task, as the **subject of a sentence** — "You", "Derya T.",
 * "delivery-bot", or null when nobody does.
 *
 * `actorLine` is the same reading as a *label* ("Derya T. — you, working on it");
 * this is the same reading as a *noun*, because a page that says "Nobody has picked
 * this up yet" directly above "Assigned to: Derya T." is stating two facts as though
 * they were one, and the reader has to work out which is wrong. Neither was: session
 * 24 (R2-21, R3-22) found both sentences on the same screen and they are two true
 * things badly worded.
 *
 * **The reader's own name is "You".** `describeActor` already knows whether the
 * holder is the person reading, and a sentence about yourself written in the third
 * person is the other half of the same complaint ("Somebody has claimed it" beside
 * "Aylin K. — you").
 *
 * Null for `none` and **not** null for a holder this page cannot name: "A studio
 * member has this" is a different answer from "nobody has this", and it is the
 * distinction `TaskWho`'s own muting exists to keep.
 */
export function holderSubject(who: ActorReading): string | null {
  if (who.mine) return 'You'
  if (who.person !== null) return who.person
  if (who.agent !== null) return who.agent
  return who.words === ASSIGNEE_WORDS.none ? null : who.words
}

/** The holder of a task, as `holderSubject` reads one. */
export function holderOf(task: TaskView, principal: Principal): string | null {
  return holderSubject(
    describeActor(
      { kind: task.assignee_kind, id: task.assignee_id, name: task.assignee_name },
      principal,
      ASSIGNEE_WORDS,
    ),
  )
}

/** True when whoever holds it is whoever is reading the page. */
export function heldByReader(task: TaskView, principal: Principal): boolean {
  return (
    task.assignee_kind === 'user' &&
    task.assignee_id !== null &&
    principal.kind !== 'anonymous' &&
    task.assignee_id === principal.userId
  )
}

export type TaskWhoProps = {
  readonly task: TaskView
  /**
   * How much of the name there is room for.
   *
   * `initials` is the board — `mockups/studio/tasks.html` draws two letters in a
   * circle and six columns leave room for nothing else. `name` is the list table
   * and the detail page, where a cell is a cell. DESIGN.md P5: the surface decides
   * how loudly a fact is stated, never the fact itself.
   */
  readonly as?: 'initials' | 'name'
  /**
   * Whether to say how firm the hold is.
   *
   * True for the board and the list, neither of which shows a status. False on the
   * detail page, where `nextMove` says it in a sentence and the Lease row gives the
   * date — P2, one fact, one owner.
   */
  readonly hold?: boolean
  /** The instant a lapsed lease is measured against. The page reads the clock once. */
  readonly at?: Date | undefined
}

/**
 * Who holds it.
 *
 * An `Avatar` only for a person, because `Avatar` is a face: initials of an agent's
 * name would say a machine is a colleague, and initials invented from a uuid would
 * be inventing a fact. Everything else is words — and the words are muted only when
 * they stand for nobody, which is what keeps "Unassigned" from reading the same as a
 * holder this page cannot name.
 */
export function TaskWho({ task, as = 'initials', hold = true, at }: TaskWhoProps) {
  const principal = usePrincipal()
  const who = describeActor(
    { kind: task.assignee_kind, id: task.assignee_id, name: task.assignee_name },
    principal,
    ASSIGNEE_WORDS,
  )
  const said = actorLine(who, hold ? holdWords(task, at ?? now()) : null)

  if (who.person !== null && as === 'initials') {
    return <Avatar name={said}>{initialsOf(who.person)}</Avatar>
  }
  /*
   * **Only "nobody" is muted.** `TaskWhen` writes "No date" in grey and a real date
   * in body text, and this is the same register: a task somebody holds is a fact
   * whether or not this page can name them, and a task nobody holds is an absence.
   * Muting both would leave "Unassigned" and "A studio member" reading alike, which
   * is the one distinction a coordinator cannot afford to lose.
   */
  return task.assignee_kind === 'none' ? <Muted>{said}</Muted> : <span>{said}</span>
}

/** `Aylin K.` → `AK`, which is the `initials` the seed gives her. The first letter of
 * the first two words, upper-cased. */
export function initialsOf(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter((part) => part !== '')
    .map((part) => part.slice(0, 1).toUpperCase())
  return letters.slice(0, 2).join('')
}

/* --------------------------------------------------------------- the card */

export type TaskCardFullProps = {
  readonly task: TaskView
  /** The `service_code`'s own name, from `catalog_list_services`. */
  readonly serviceName?: string | undefined
  /**
   * The instant the board was drawn at. Passed in rather than read here so that
   * every card on one board agrees about what "late" means, and so a test can move
   * it.
   */
  readonly at?: Date | undefined
  /**
   * Primary + overflow moves (`RowActions` on the board). Optional so the card
   * primitive stays a picture in gallery/tests that only care about the reading.
   */
  readonly actions?: ReactNode | undefined
  /**
   * What the page says when the date in this card's cell is written, or refused.
   *
   * Handed in rather than read from `taskNotice.tsx`, because a card is a primitive
   * and a primitive that reaches into a page for its words cannot be drawn anywhere
   * else — the gallery's story draws this card with neither. Both are optional and
   * both **matter**: without the second, `CellDue`'s own `onError?.()` goes nowhere
   * and a refused write on a board card says nothing at all.
   *
   * `since` is `CellDue`'s, passed straight through: the stamp the save answered
   * with, which the page's Undo sends back so it cannot overwrite a later change.
   */
  readonly onDateSaved?:
    ((previous: string | null, next: string | null, since: string | null) => void) | undefined
  readonly onDateError?: ((message: string) => void) | undefined
}

/**
 * The board card — `mockups/studio/tasks.html`'s `.tcard`, filled from a `TaskView`.
 *
 * **The title is the link**, not the card. Everything a card leaves out is on the
 * detail page, so a card with no way through would make the board a picture; and the
 * link is on the title rather than around the whole block because a `.tcard` already
 * contains one interactive element and nesting them is what makes a card
 * unkeyboardable. `Linkish asChild` with a router `Link`, per CONTRACT.md
 * §Internal links.
 *
 * Inline actions (#5) sit in `meta` beside Who / Due — still separate controls, not
 * a clickable card wrapper. Who opens Assign (#6), Due opens the date editor (#7),
 * and the note opens Edit the brief (#8) when it is not a block reason — same as
 * the list cells.
 */
export function TaskCardFull({
  task,
  serviceName,
  at,
  actions,
  onDateSaved,
  onDateError,
}: TaskCardFullProps) {
  const measured = at ?? now()
  const instructions = task.instructions === '' ? null : task.instructions
  const noteNode =
    task.blocked_reason !== null ? (
      task.blocked_reason
    ) : (
      <ActionDialog
        action="tasks_update"
        label="Edit notes"
        title="Edit notes"
        submitLabel="Save"
        fixed={{ task_ref: task.ref }}
        only={['instructions']}
        defaults={{ instructions: task.instructions }}
        trigger={
          <MetaTrigger aria-label={`Edit notes for ${task.ref}`} title={instructions ?? undefined}>
            {instructions === null ? <Muted>—</Muted> : notePreview(instructions)}
          </MetaTrigger>
        }
      />
    )

  return (
    <TaskCard
      id={task.ref.toLowerCase()}
      state={taskState(task, measured)}
      client={taskUnder(task, serviceName)}
      title={
        <Linkish asChild>
          <Link to="/studio/tasks/$ref" params={{ ref: task.ref }} title={task.title}>
            {task.title}
          </Link>
        </Linkish>
      }
      note={noteNode}
      meta={
        <>
          <AssignDialog
            task={task}
            trigger={
              <MetaTrigger aria-label={`Assign ${task.ref}`}>
                <TaskWho task={task} at={measured} />
              </MetaTrigger>
            }
          />
          {/*
            The same one-click date as the list (session 38). Who and the note keep
            their dialogs here on purpose: a card's Who is the `.avatar` with its
            initials, which `mockups/assets/CONTRACT.md` §2.12 names and
            `mockups/studio/tasks.html` draws, and a select in its place would be a
            change to the card's anatomy rather than to how a date is edited.
          */}
          <CellDue
            taskRef={task.ref}
            value={task.due_at}
            stamp={task.updated_at}
            label={`Change due date for ${task.ref}`}
            /* The card's date, or the one on its way to it: one reading for both. */
            display={(dueAt) => <TaskWhen task={{ ...task, due_at: dueAt }} at={measured} />}
            onSaved={onDateSaved}
            onError={onDateError}
          />
          {task.money === null ? null : <MoneyTag amount={task.money} />}
          {actions ?? null}
        </>
      }
    />
  )
}

/**
 * Quiet button around Who / the note so the dialog trigger looks like the meta chip.
 *
 * `.cell-btn` rather than `.th-sort`: a card has no columns to sort, and the class a
 * sortable table head wears was never a statement this button could make.
 */
const MetaTrigger = forwardRef<
  HTMLButtonElement,
  { readonly children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>
>(function MetaTrigger({ children, className, type, ...props }, ref) {
  return (
    <button ref={ref} type={type ?? 'button'} className={className ?? 'cell-btn'} {...props}>
      {children}
    </button>
  )
})
